import classNames from "classnames";
import { useFieldWithInitialValue } from "Components/Hooks";
import { useField, useFormik, useFormikContext } from "formik";
import React from "react";
import { doesMetaHaveError, renderErrorMessage } from "./Tools";

export function RadioYesNoBool(props: {
  fieldName: string;
  children?: React.ReactNode;
  isReadonly?: boolean;
}) {
  return (
    <Radio
      {...props}
      options={[
        ["true", "Yes"],
        ["false", "No"],
      ]}
    />
  );
}

export function RadioYesNo(props: {
  fieldName: string;
  children?: React.ReactNode;
  isReadonly?: boolean;
}) {
  return (
    <Radio
      {...props}
      options={[
        ["Yes", "Yes"],
        ["No", "No"],
      ]}
    />
  );
}

export function Radio<T extends string>(props: {
  fieldName: string;
  children?: React.ReactNode;
  options: [T, React.ReactNode][];
  isReadonly?: boolean;
}) {
  const form = useFormikContext();
  const [field, meta] = useField(props.fieldName);
  return (
    <div className="field is-narrow">
      <div className="control">
        {props.children}
        <span style={{ position: "relative", top: "-1.5px" }}>
          {props.options.map(([key, value], i) => {
            const id = `${props.fieldName}_${key}`;
            return (
              <React.Fragment key={id}>
                <input
                  className={classNames("is-checkradio", {
                    "input-validation-error": doesMetaHaveError(meta),
                  })}
                  id={id}
                  type="radio"
                  checked={field.value === key}
                  {...field}
                  onChange={() => {
                    form.setFieldValue(field.name, key);
                  }}
                  value={key?.toString()}
                  disabled={props.isReadonly}
                />
                <label htmlFor={id}>{value}</label>
                {i === Object.keys(props.options).length - 1 &&
                  renderErrorMessage(meta)}
              </React.Fragment>
            );
          })}
          {props.isReadonly && <input type="hidden" {...field} />}
        </span>
      </div>
    </div>
  );
}

interface LabelAndInputProps {
  label: React.ReactNode;
  inputProps?: React.DetailedHTMLProps<
    React.InputHTMLAttributes<HTMLInputElement>,
    HTMLInputElement
  >;
}

export function VerticalRadio(props: {
  fieldName: string;
  options: [string, React.ReactNode | LabelAndInputProps][];
  value?: string;
  hasBorderedArea?: true;
}) {
  const [field, meta] = useFieldWithInitialValue(props.fieldName, props.value);
  const error = renderErrorMessage(meta);
  return (
    <div
      className={classNames({
        "vertical-radio-has-bordered-area": props.hasBorderedArea,
      })}
    >
      {props.options.map(([value, option], i) => {
        const { label, inputProps } =
          option !== null && typeof option === "object" && "label" in option
            ? option
            : { label: option, inputProps: {} };
        const id = `${props.fieldName}_${value}`;
        const isSelected = field.value?.toString() === value;
        return (
          <label key={value}>
            <div
              key={id}
              className={classNames("field vertical-radio-option", {
                "is-selected": isSelected,
              })}
              style={{ marginTop: i > 0 ? "0.75em" : undefined }}
            >
              <div style={{ flexShrink: 0 }}>
                <input
                  {...inputProps}
                  {...field}
                  className={classNames("is-checkradio", {
                    "input-validation-error": doesMetaHaveError(meta),
                  })}
                  id={id}
                  type="radio"
                  checked={isSelected}
                  value={value}
                />
                {/* NOTE: the following label is to get bulma-checkradio to render */}
                <label style={{ paddingLeft: "1em", pointerEvents: "none" }} />
              </div>
              <div>{label}</div>
            </div>
          </label>
        );
      })}
      {error && <div>{error}</div>}
    </div>
  );
}
